<div class="bg-body-tertiary min-vh-100 d-flex align-items-start pt-5">
    <div class="mx-auto w-100" style="max-width: 640px; padding: 16px;">
        <c-card>
            <c-card-body class="p-4">
                <ng-container *ngTemplateOutlet="loginTemplate"></ng-container>
            </c-card-body>
        </c-card>
    </div>
</div>

<ng-template #loginTemplate>
    <h3 class="text-center mb-4">Welcome to Scion</h3>

    @if (authUser() || notAccessToAccount()) {

        <!-- User info bar -->
        <div class="d-flex align-items-center gap-3 p-3 border rounded mb-4">
            <c-avatar shape="rounded-1" size="md" [src]="authUser()?.picture"></c-avatar>
            <div style="flex: 1; min-width: 0; overflow: hidden;">
                <div class="fw-semibold text-truncate">{{ authUser()?.email }}</div>
                <div class="small text-muted">{{ roleLabel() }}</div>
            </div>
        </div>

        @if (notAccessToAccount()) {
            <p class="text-center text-muted">
                You are not part of any organization yet. Please ask to be invited.
            </p>
        } @else if (isRoleLoading()) {
            <div class="text-center py-4">
                <c-spinner aria-hidden="true"></c-spinner>
            </div>
        } @else {

            <!-- Step 1: Role picker (multiple role types available) -->
            @if (isRolePickerNeeded() && !isRoleSelected()) {
                <p class="text-muted small mb-2">Select a role to continue</p>
                <ng-container *ngTemplateOutlet="rolePicker"></ng-container>
            }

            <!-- Steps 2 & 3 -->
            @if (isRoleSelected()) {
                @if (!selectedAccount()) {
                    @if (skippedAccountOrgs(); as orgs) {
                        <!-- Single account + account_id set: show org list directly -->
                        <p class="text-muted small mb-2">Select an Organization to continue</p>
                        <div class="border rounded overflow-hidden">
                            @for (org of orgs; track org.org_id; let last = $last) {
                                <div class="login-select-item login-select-item--compact"
                                     [class.border-bottom]="!last"
                                     role="button" tabindex="0"
                                     (click)="useOrganization(org)"
                                     (keydown.enter)="useOrganization(org)"
                                     (keydown.space)="useOrganization(org)">
                                    <div class="login-select-item__content">
                                        <div class="login-select-item__title">{{ org.name }}</div>
                                    </div>
                                    <svg cIcon name="cilChevronRight" size="sm" class="text-muted flex-shrink-0"></svg>
                                </div>
                            }
                        </div>
                    } @else {
                        <ng-container *ngTemplateOutlet="accountList"></ng-container>
                    }
                } @else {
                    <div class="text-center py-4">
                        <c-spinner aria-hidden="true"></c-spinner>
                    </div>
                }
            }
        }

        <div class="d-flex align-items-center justify-content-between mt-4 pt-3 border-top">
            @if (isRoleSelected() && isRolePickerNeeded() && !selectedAccount()) {
                <button (click)="clearRoleSelection()" cButton size="sm" color="secondary">Back</button>
            } @else if (selectedAccount()) {
                <div class="d-flex gap-2">
                    <button (click)="clearAccountSelection()" cButton size="sm" color="secondary">Back</button>
                    @if (isRolePickerNeeded()) {
                        <button (click)="clearRoleSelection()" cButton size="sm" color="secondary">Change role</button>
                    }
                </div>
            } @else {
                <span></span>
            }
            <button (click)="logout()" cButton size="sm" color="secondary">Sign out</button>
        </div>

    } @else {
        <p class="text-center text-muted mb-4">Sign in to manage your data</p>
        <div class="d-grid">
            <button (click)="login()" cButton color="primary" size="lg">Sign In</button>
        </div>
    }
</ng-template>

<ng-template #rolePicker>
    <div class="border rounded overflow-hidden">
        @for (role of availableRoles(); track role; let last = $last) {
            <div class="login-select-item" [class.border-bottom]="!last"
                 role="button" tabindex="0"
                 (click)="assumeRole(role)"
                 (keydown.enter)="assumeRole(role)"
                 (keydown.space)="assumeRole(role)">
                <div class="login-select-item__content">
                    <div class="login-select-item__title fw-semibold">{{ roleInfo[role]?.title }}</div>
                    <div class="login-select-item__subtitle small text-muted">{{ roleInfo[role]?.subtitle }}</div>
                </div>
                <svg cIcon name="cilChevronRight" size="sm" class="text-muted flex-shrink-0"></svg>
            </div>
        }
    </div>
</ng-template>

<ng-template #accountList>
    @if (accountOptionsError()) {
        <p class="text-center text-danger">Failed to load accounts.</p>
    } @else if (!accountOptions()) {
        <div class="text-center py-4"><c-spinner aria-hidden="true"></c-spinner></div>
    } @else if (accountOptions()?.length) {
        <p class="text-muted small mb-2">{{ needsOrgStep() ? 'Select an Account and Organization to continue' : 'Select an Account to continue' }}</p>
        <div class="border rounded overflow-hidden">
            @for (account of accountOptions(); track account.id; let last = $last) {
                <!-- Account row -->
                <div class="login-select-item login-select-item--compact"
                     [class.border-bottom]="!last || expandedAccounts().has(account.id)"
                     role="button" tabindex="0"
                     (click)="selectAccount(account)"
                     (keydown.enter)="selectAccount(account)"
                     (keydown.space)="selectAccount(account)">
                    <div class="login-select-item__content">
                        <div class="login-select-item__title fw-semibold">{{ account.name }}</div>
                    </div>
                    @if (needsOrgStep() && expandedAccounts().has(account.id)) {
                        <svg cIcon name="cilChevronBottom" size="sm" class="text-muted flex-shrink-0"></svg>
                    } @else {
                        <svg cIcon name="cilChevronRight" size="sm" class="text-muted flex-shrink-0"></svg>
                    }
                </div>

                <!-- Inline org list (expanded) -->
                @if (needsOrgStep() && expandedAccounts().has(account.id)) {
                    @if (orgErrors().has(account.id)) {
                        <div class="px-3 py-2 small text-danger" [class.border-bottom]="!last">Failed to load organizations.</div>
                    } @else if (expandedAccounts().get(account.id) === null) {
                        <div class="text-center py-2" [class.border-bottom]="!last">
                            <c-spinner aria-hidden="true" size="sm"></c-spinner>
                        </div>
                    } @else if (expandedAccounts().get(account.id)?.length) {
                        @for (org of expandedAccounts().get(account.id)!; track org.org_id; let orgLast = $last) {
                            <div class="login-select-item login-select-item--compact login-select-item--org"
                                 [class.border-bottom]="!orgLast || !last"
                                 role="button" tabindex="0"
                                 (click)="useOrganization(org)"
                                 (keydown.enter)="useOrganization(org)"
                                 (keydown.space)="useOrganization(org)">
                                <div class="login-select-item__content">
                                    <div class="login-select-item__title">{{ org.name }}</div>
                                </div>
                                <svg cIcon name="cilChevronRight" size="sm" class="text-muted flex-shrink-0"></svg>
                            </div>
                        }
                    } @else {
                        <div class="px-3 py-2 small text-muted" [class.border-bottom]="!last">No organizations found.</div>
                    }
                }
            }
        </div>
    } @else {
        <p class="text-center text-muted">No accounts found.</p>
    }
</ng-template>
